import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '@/lib/api';
import { linkify } from '@/lib/linkify';
import { usePresentation } from '@/context/PresentationContext';
import { CalendarBlank, HouseLine, Warning, Sparkle, ClipboardText, ArrowRight } from '@phosphor-icons/react';
import TanowMark from '@/components/TanowMark';
import AddToHomeScreenTip from '@/components/AddToHomeScreenTip';

const HERO_STYLE_MAP = {
    'Celebrate': { bg: '#FEF3C7', border: '#F59E0B', accent: '#B45309', emoji: '🎉' },
    'Gentle reminder': { bg: '#FEF9C3', border: '#EAB308', accent: '#854D0E', emoji: '💛' },
    'Heads up': { bg: '#DBEAFE', border: '#3B82F6', accent: '#1E40AF', emoji: '💙' },
};

function timeSince(iso) {
    if (!iso) return 'a moment ago';
    const dt = new Date(iso);
    const now = new Date();
    const mins = Math.floor((now - dt) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min`;
    const hours = Math.floor(mins / 60);
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const then = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const dayDiff = Math.round((today - then) / 86400000);
    if (dayDiff === 0) {
        const h = dt.getHours();
        if (h < 12) return 'since this morning';
        if (h < 18) return 'since this afternoon';
        return 'since this evening';
    }
    if (dayDiff === 1) return 'since yesterday';
    if (dayDiff < 7) return `${dayDiff} days`;
    return `since ${dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function formatDay(dt) {
    if (!dt) return { day: 'When you\u2019re ready', time: '' };
    const d = new Date(dt);
    return {
        day: d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }),
        time: d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
    };
}

function useClock() {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 30_000);
        return () => clearInterval(t);
    }, []);
    return now;
}

export default function HouseholdBoard() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const now = useClock();
    const { maskHousehold, maskName, on: presentationOn } = usePresentation();

    useEffect(() => {
        let cancel = false;
        const load = async () => {
            try {
                const { data } = await api.get(`/board/${token}`);
                if (!cancel) { setData(data); setErr(null); }
            } catch (e) {
                if (!cancel) setErr(e.response?.data?.detail || 'Board not found');
            }
        };
        load();
        // Auto-refresh every 20s — fast enough for a rule-posted hero card to appear
        // shortly after a family member answers a check-in on their phone.
        const t = setInterval(load, 20_000);
        return () => { cancel = true; clearInterval(t); };
    }, [token]);

    if (err) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white text-slate-800 text-2xl p-8 text-center">
                {err}
            </div>
        );
    }
    if (!data) {
        return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
    }

    const today = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    const clock = now.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

    return (
        <div className="board-screen min-h-screen bg-[#FBF7EF] text-slate-900">
            <AddToHomeScreenTip startUrl={`/board/${token}`} tone="dark" />
            {/* Small, unobtrusive demo notice — only shows for fictional families */}
            {data?.is_demo_household && (
                <div data-testid="board-demo-notice" className="w-full flex items-center justify-center gap-2 bg-amber-100 text-amber-900 text-[11px] py-1.5 border-b border-amber-200">
                    Demo data — this family is fictional.
                </div>
            )}

            <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
                {/* Today */}
                <div className="flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="text-[13px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2">
                            <HouseLine size={16} weight="duotone" /> {maskHousehold(data.household.name)}
                        </div>
                        <h1 className="font-heading text-6xl md:text-7xl leading-[1.05] text-slate-900 mt-2">{today}</h1>
                    </div>
                    <div className="font-heading text-6xl md:text-7xl text-primary tabular-nums" data-testid="board-clock">{clock}</div>
                </div>

                {/* Hero card — most prominent, above everything */}
                {data.hero_card && (() => {
                    const s = HERO_STYLE_MAP[data.hero_card.style] || HERO_STYLE_MAP['Celebrate'];
                    return (
                        <section
                            data-testid="board-hero-card"
                            className="mt-8 rounded-3xl px-8 md:px-10 py-8 shadow-md relative overflow-hidden"
                            style={{ background: s.bg, border: `3px solid ${s.border}`, transform: 'rotate(-0.35deg)' }}
                        >
                            <div className="flex items-center gap-2 text-[12px] uppercase tracking-[0.3em] font-semibold" style={{ color: s.accent }}>
                                <Sparkle size={14} weight="duotone" /> {data.hero_card.style}
                            </div>
                            <div className="mt-3 font-heading text-4xl md:text-5xl leading-tight text-slate-900 whitespace-pre-wrap">
                                {presentationOn ? <em className="text-slate-400 not-italic">Hidden in presentation mode</em> : linkify(data.hero_card.message)}
                            </div>
                            <div className="mt-4 text-sm" style={{ color: s.accent }}>
                                {data.hero_card.created_by_name || 'Coordinator'} · {new Date(data.hero_card.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                            </div>
                        </section>
                    );
                })()}

                {/* Check-ins waiting — the only tappable elements on the board */}
                <section className="mt-10">
                    <div className="text-[13px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2 mb-4">
                        <ClipboardText size={16} weight="duotone" /> Check-ins waiting
                    </div>
                    {(data.pending_check_ins || []).length === 0 ? (
                        <div data-testid="board-checkins-empty" className="rounded-2xl border-2 border-dashed border-emerald-200 bg-emerald-50/50 py-8 text-center text-2xl text-emerald-800">
                            All caught up 🎉
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {data.pending_check_ins.map(p => {
                                const maskedName = maskName(p.patient_name || '');
                                const first = (maskedName || '').split(' ')[0];
                                const waitedSince = timeSince(p.assigned_at);
                                return (
                                    <a
                                        key={p.assignment_id}
                                        data-testid="board-pending-check-in"
                                        href={`/check-in/${p.token}?back=/board/${token}`}
                                        className="group rounded-2xl bg-white border-2 border-primary/40 hover:border-primary px-6 py-5 shadow-md hover:shadow-lg transition-shadow duration-150 flex items-center gap-4"
                                    >
                                        <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                            <ClipboardText size={26} weight="duotone" className="text-primary" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-xs uppercase tracking-widest text-slate-500">For</div>
                                            <div className="font-heading text-3xl md:text-4xl text-slate-900 leading-tight">{first || maskedName}</div>
                                            <div className="text-lg text-slate-700 mt-1">{p.survey_name}</div>
                                            <div className="text-sm text-slate-500 mt-1">waiting {waitedSince}</div>
                                        </div>
                                        <ArrowRight size={26} weight="bold" className="text-primary shrink-0 group-hover:translate-x-1 transition-transform duration-150" />
                                    </a>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Coming up */}
                <section className="mt-12">
                    <div className="text-[13px] uppercase tracking-[0.3em] text-slate-500 flex items-center gap-2 mb-4">
                        <CalendarBlank size={16} weight="duotone" /> Coming up
                    </div>
                    {data.upcoming_appointments.length === 0 ? (
                        <div className="rounded-2xl border-2 border-dashed border-slate-300 py-10 text-center text-2xl text-slate-500">
                            Nothing on the calendar right now.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {data.upcoming_appointments.slice(0, 5).map(a => {
                                const { day, time } = formatDay(a.scheduled_at);
                                return (
                                    <div key={a.appointment_id} data-testid="board-appt" className="rounded-2xl bg-white border-2 border-slate-200 px-6 py-5 shadow-sm flex items-center gap-6 flex-wrap">
                                        <div className="min-w-[220px]">
                                            <div className="font-heading text-4xl text-slate-900">{day}</div>
                                            {time && <div className="text-2xl text-primary mt-1">{time}</div>}
                                        </div>
                                        <div className="flex-1 min-w-[240px]">
                                            <div className="text-2xl md:text-3xl text-slate-900 font-medium">{a.title}</div>
                                            <div className="text-lg text-slate-600 mt-1">
                                                {a.provider_name || 'Doctor'}
                                                {a.patient_name && ` · for ${maskName(a.patient_name)}`}
                                            </div>
                                            {a.note && (
                                                <div className="mt-3 inline-block rounded-full bg-amber-100 text-amber-900 px-4 py-1.5 text-lg font-medium">
                                                    {a.note}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </section>

                {/* Board notes */}
                <section className="mt-12">
                    <div className="text-[13px] uppercase tracking-[0.3em] text-slate-500 mb-4">Notes from the family</div>
                    {data.board_notes.length === 0 ? (
                        <div className="rounded-2xl border-2 border-dashed border-slate-300 py-10 text-center text-2xl text-slate-500">
                            Nothing on the board yet.
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-4">
                            {data.board_notes.slice(0, 6).map(n => (
                                <div key={n.board_note_id} data-testid="board-note" className="rounded-2xl bg-white border-2 border-amber-200 px-6 py-5 shadow-sm">
                                    <div className="text-2xl md:text-3xl leading-snug text-slate-900 whitespace-pre-wrap">{presentationOn ? <em className="text-slate-400 not-italic">Hidden in presentation mode</em> : linkify(n.text)}</div>
                                    <div className="mt-3 text-sm text-slate-500">
                                        {n.author_name || 'Family'} · {new Date(n.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </section>

                {data.medications?.length > 0 && (
                    <section className="mt-12" data-testid="board-medications">
                        <div className="flex items-center gap-2 text-[13px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Medication schedule</div>
                        <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
                            {data.medications.map((m, i) => (
                                <div key={i} className="px-6 py-4 flex items-center gap-4 flex-wrap text-xl">
                                    <span className="text-slate-900 font-medium">{m.name}</span>
                                    <span className="text-slate-500">for {maskName(m.patient_name)}</span>
                                    <span className="ml-auto text-slate-700">{m.times.map(t => { const [h, mi] = t.split(':').map(Number); return `${((h + 11) % 12) + 1}:${String(mi).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`; }).join(' · ')}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Emergency line */}
                <div className="mt-14 border-t-2 border-slate-300 pt-4 flex items-center gap-3 text-lg text-slate-700">
                    <Warning size={22} weight="fill" className="text-red-600" />
                    <div>
                        <b>This board is not monitored 24/7.</b> If this is an emergency, call <b>911</b>.
                    </div>
                </div>
                <div className="mt-6 flex items-center gap-2 text-sm text-slate-500" data-testid="board-brand">
                    <TanowMark size={18} /> <span className="font-heading text-base text-slate-700">Tanow</span> · Family care, coordinated.
                </div>
            </div>
        </div>
    );
}
